import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { 
  Home, 
  Users, 
  Clock, 
  FileText, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  Building2,
  User,
  ChevronDown
} from 'lucide-react';

const Navbar: React.FC = () => {
  const { currentUser, logout, hasAnyPermission } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      setIsUserMenuOpen(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const navigation = [
    { name: 'Dashboard', href: '/', icon: Home, permissions: [] }, // Everyone can see dashboard
    { name: 'Timesheets', href: '/timesheets', icon: Clock, permissions: ['view_timesheets', 'view_own_timesheets', 'submit_timesheets'] },
    { name: 'Invoices', href: '/invoices', icon: FileText, permissions: ['view_invoices', 'view_own_invoices'] },
    { name: 'Employees', href: '/employees', icon: Users, permissions: ['view_employees'] },
    { name: 'Contractors', href: '/contractors', icon: Building2, permissions: ['view_contractors'] },
    { name: 'Users', href: '/users', icon: Settings, permissions: ['manage_users'] },
  ];

  const filteredNavigation = navigation.filter(item => {
    if (!currentUser) return false;
    if (item.permissions.length === 0) return true; // Everyone can see items with no permissions
    
    // Use the proper hasAnyPermission function that includes admin bypass
    return hasAnyPermission(item.permissions as any[]);
  });

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  if (!currentUser) {
    return null;
  }

  return (
    <nav className="bg-white shadow-lg border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-full mx-auto px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo and brand */}
          <div className="flex items-center flex-shrink-0">
            <Link to="/" className="flex items-center space-x-4 group">
              <div className="relative">
                <img 
                  src="/logo.png" 
                  alt="7Twenty4 Services" 
                  className="h-12 w-12 object-contain group-hover:scale-105 transition-transform duration-200"
                />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-2xl font-bold text-gray-900 group-hover:text-amber-600 transition-colors duration-200">
                  7Twenty4 Services
                </h1>
              </div>
            </Link>
          </div>

          {/* Desktop navigation */}
          <div className="hidden lg:flex items-center space-x-2">
            {filteredNavigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-2 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 relative group ${
                    active
                      ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/25 scale-105'
                      : 'text-gray-600 hover:text-amber-600 hover:bg-amber-50'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${active ? 'text-white' : 'text-gray-500 group-hover:text-amber-600'}`} />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </div>

          {/* User menu */}
          <div className="hidden lg:flex items-center space-x-4">
            <div className="relative">
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center space-x-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl border border-gray-200 transition-all duration-200 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center shadow-md">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div className="flex flex-col items-start">
                  <span className="text-sm font-semibold text-gray-900 leading-tight">
                    {currentUser.displayName || currentUser.email?.split('@')[0]}
                  </span>
                  <span className="text-xs text-amber-600 font-medium capitalize leading-tight">
                    {currentUser.role.replace('_', ' ')}
                  </span>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* User dropdown */}
              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-3 px-4 py-3 text-sm font-medium text-gray-700 hover:text-red-600 hover:bg-red-50 transition-colors duration-200 w-full text-left"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-600 hover:text-amber-600 p-3 rounded-xl hover:bg-amber-50 transition-all duration-200"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-6 py-6 space-y-3">
            {filteredNavigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center space-x-4 px-4 py-4 rounded-xl text-base font-medium transition-all duration-200 ${
                    active
                      ? 'bg-amber-500 text-white shadow-lg'
                      : 'text-gray-600 hover:text-amber-600 hover:bg-amber-50'
                  }`}
                >
                  <Icon className={`w-6 h-6 ${active ? 'text-white' : 'text-gray-500'}`} />
                  <span>{item.name}</span>
                </Link>
              );
            })}
            
            {/* Mobile user info */}
            <div className="border-t border-gray-200 pt-6 mt-6">
              <div className="flex items-center space-x-4 px-4 py-4 bg-gray-50 rounded-xl mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center shadow-md">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="text-base font-semibold text-gray-900">
                    {currentUser.displayName || currentUser.email?.split('@')[0]}
                  </span>
                  <span className="text-sm text-amber-600 font-medium capitalize">
                    {currentUser.role.replace('_', ' ')}
                  </span>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-4 px-4 py-4 rounded-xl text-base font-medium text-gray-700 hover:text-red-600 hover:bg-red-50 transition-all duration-200 w-full"
              >
                <LogOut className="w-6 h-6" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
